import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { LogIn, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { APP_VERSION, BUILD_DATE } from "@/version";

const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_401d88f4-8dae-48a8-9716-45cb5be0ec5c/artifacts/4x1k75zi_Livanespor_SARI_SIYAH_NEW%20genelde%20bu.png";

const AdminLogin = () => {
    const { user, loading: authLoading, login } = useAuth();
    const { branding, loaded: brandingLoaded } = useBranding();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => { if (user) navigate("/admin/dashboard", { replace: true }); }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            await login(email.trim(), password);
            toast.success("Giriş başarılı");
            // Full reload so branding (club identity, plan, theme) is re-fetched for this user
            window.location.href = "/admin/dashboard";
        } catch (e2) {
            const msg = e2?.response?.data?.detail || "Giriş başarısız";
            setErr(msg);
            toast.error(msg);
        } finally { setLoading(false); }
    };

    if (authLoading || !brandingLoaded) return <div className="min-h-screen bg-liv-black flex items-center justify-center text-neutral-400">Yükleniyor…</div>;

    const isPlatform = !!branding?.is_platform;
    const clubLogo = isPlatform ? "" : (branding?.logo_url || (branding?.id === "livanespor" ? FALLBACK_LOGO : ""));
    const clubName = isPlatform ? "DR AI FUTBOL" : (branding?.name || "Kulüp");
    const clubShort = isPlatform ? "DR" : (branding?.short_name || clubName);
    // Split name into two lines for the big block (e.g. "Livane Spor" → "Livane / Spor")
    const nameLines = (() => {
        const parts = clubName.trim().split(/\s+/);
        if (parts.length >= 2) return [parts[0], parts.slice(1).join(" ")];
        // Single word — split at midpoint
        const mid = Math.ceil(parts[0].length / 2);
        return [parts[0].slice(0, mid), parts[0].slice(mid)];
    })();
    const initials = clubShort.replace(/\s+/g, "").substring(0, 2).toUpperCase();

    return (
        <div className="min-h-screen bg-liv-black text-white grid grid-cols-1 md:grid-cols-2" data-testid="admin-login-page">
            <div className="hidden md:flex relative items-end p-12 bg-liv-yellow text-black overflow-hidden">
                <div className="absolute top-0 right-0 font-display text-[28vw] leading-none text-black/5 select-none">{initials}</div>
                <div className="relative z-10">
                    {clubLogo ? <img src={clubLogo} alt={clubName} className="w-20 h-20 object-contain" /> : null}
                    <div className="font-display text-[10vw] xl:text-[9vw] leading-[0.85] uppercase mt-6">
                        {nameLines[0]}<br/>{nameLines[1]}
                    </div>
                    <div className="mt-4 text-sm font-bold uppercase tracking-[0.3em]">Admin Panel · {new Date().getFullYear()}</div>
                </div>
            </div>
            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-sm">
                    {clubLogo ? <img src={clubLogo} alt={clubName} className="w-14 h-14 md:hidden mb-6 object-contain" /> : null}
                    <div className="overline">Yönetim</div>
                    <h1 className="font-display text-5xl uppercase mt-2">Giriş Yap</h1>
                    <p className="text-sm text-neutral-400 mt-2">{isPlatform ? "Kulüp yönetim paneline erişmek için kulübünüze tanımlı kullanıcı adı ve şifrenizi girin." : `${clubName} admin paneline erişmek için kullanıcı adı ve şifrenizi girin.`}</p>
                    {err && <div className="mt-4 p-3 border border-red-700 bg-red-950/40 text-red-300 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>}
                    <div className="mt-6">
                        <label className="liv-label">Kullanıcı Adı</label>
                        <input type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="liv-input" required data-testid="admin-login-email" />
                    </div>
                    <div className="mt-4">
                        <label className="liv-label">Şifre</label>
                        <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="liv-input" required data-testid="admin-login-password" />
                    </div>
                    <button disabled={loading} type="submit" className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="admin-login-submit">
                        <LogIn className="w-4 h-4" /> {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
                    </button>
                    <div className="mt-8 pt-4 border-t border-liv-border/60 flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-500" data-testid="admin-login-version">
                        <span>Sürüm <span className="text-liv-yellow font-bold">v{APP_VERSION}</span></span>
                        <span className="text-neutral-600">Build {BUILD_DATE}</span>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default AdminLogin;
